import React, { useState, useEffect } from 'react';
import { Users, UserCircle2, Mail, Phone, Briefcase } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const InitialAvatar = ({ name, className }) => (
  <div className={`flex items-center justify-center rounded-full bg-primary-50 font-bold text-primary-600 ${className}`}>
    {(name || '?')[0].toUpperCase()}
  </div>
);

const ContactLine = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1.5 text-xs text-gray-500">
    <Icon className="h-3.5 w-3.5 shrink-0" />
    <span className="truncate">{children}</span>
  </div>
);

const MyTeam = () => {
  const [reportingManager, setReportingManager] = useState(null);
  const [subordinates, setSubordinates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const fetchMyTeam = async () => {
    try {
      const response = await api.get('/employee/my-team');
      setReportingManager(response.data.reportingManager || null);
      setSubordinates(response.data.subordinates || []);
    } catch (error) {
      console.error('Error fetching my team:', error);
      toast.error('Failed to fetch team details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="mt-8" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Team</h1>
        <p className="text-gray-600">Your reporting manager and direct reports</p>
      </div>

      {/* Your Reporting Manager */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Your Reporting Manager</h3>
          <p className="text-sm text-gray-500 mt-1">The person you report to</p>
        </div>
        <div className="p-6">
          {reportingManager ? (
            <div className="flex items-start gap-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
              <InitialAvatar name={reportingManager.name} className="h-12 w-12 text-lg flex-shrink-0" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-gray-900">{reportingManager.name}</p>
                  <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                    {reportingManager.employeeId || 'N/A'}
                  </span>
                </div>
                {reportingManager.jobTitle && (
                  <ContactLine icon={Briefcase}>{reportingManager.jobTitle}</ContactLine>
                )}
                {reportingManager.email && (
                  <ContactLine icon={Mail}>{reportingManager.email}</ContactLine>
                )}
                {reportingManager.mobileNumber && (
                  <ContactLine icon={Phone}>{reportingManager.mobileNumber}</ContactLine>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <UserCircle2 className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-500">No reporting manager assigned</p>
              <p className="mt-1 text-xs text-gray-400">Contact HR if you believe this is incorrect</p>
            </div>
          )}
        </div>
      </div>

      {/* Your Subordinates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Your Subordinates</h3>
            <p className="text-sm text-gray-500 mt-1">Employees who report to you</p>
          </div>
          <div className="flex items-center px-3 py-1.5 bg-primary-50 rounded-lg">
            <Users className="h-4 w-4 text-primary-600 mr-1.5" />
            <span className="text-sm font-medium text-primary-700">
              {subordinates.length} {subordinates.length === 1 ? 'Report' : 'Reports'}
            </span>
          </div>
        </div>
        <div className="p-6">
          {subordinates.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {subordinates.map((member) => (
                <div key={member._id} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <InitialAvatar name={member.name} className="h-10 w-10 text-sm flex-shrink-0" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-gray-900 truncate">{member.name}</p>
                      <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {member.employeeId || 'N/A'}
                      </span>
                    </div>
                    {member.jobTitle && <ContactLine icon={Briefcase}>{member.jobTitle}</ContactLine>}
                    {member.email && <ContactLine icon={Mail}>{member.email}</ContactLine>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-500">No direct reports</p>
              <p className="mt-1 text-xs text-gray-400">Employees assigned to report to you will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyTeam;
